import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import useDateRangeFilter from '../../../../hooks/useDateRangeFilter'
import useQueryParams from '../../../../hooks/useQueryParams'

import PullAffiliateModal from '../../../../components/PullAffiliateModal/PullAffiliateModal'
import callAPI from '../../../../helpers/callAPI'
import Affiliate from '../../../../templates/Affiliate/Affiliate'
import Dollars from '../../../../templates/Dollars/Dollars'
import Number from '../../../../templates/Number/Number'
import ReportTable from '../../../../templates/ReportTable/ReportTable'
import Loading from '../../../../templates/UI/Loading/Loading'
import ReportsStatsDropdown from '../../Templates/ReportsStatsDropdown/ReportsStatsDropdown'
import numberSort from '../../../../helpers/numberSort'

export default function AffiliateReport({ showSystemMessage, setAffiliate, setCampaign, setAdvertiser, setCSVFilename, setCSVHeaderLabels, setCSVDataLabels, setCSVReport, marginFilterOperator, marginFilterValue }) {
    const { initStartDate, initEndDate } = useDateRangeFilter()
    const { campaignId, advertiserId, withAvatar, navigation, buildUrl, hideNetwork } = useQueryParams()
    const [dataLoaded, setDataLoaded] = useState(false)
    const [report, setReport] = useState([])
    const [totals, setTotals] = useState([])
    const [hasForex, setHasForex] = useState(false)
    const [forexCurrency, setForexCurrency] = useState(null)

    const [pullAffiliateModalOpen, setPullAffiliateModalOpen] = useState(false)
    const [pullAffiliateModalData, setPullAffiliateModalData] = useState({})

    useEffect(() => {
        getReport()
    }, [campaignId, hideNetwork])

    useEffect(() => {
        if (hideNetwork === 'true') {
            setCSVHeaderLabels(['Affiliate Id', 'Affiliate', 'Clicks', 'Conversions', 'Conversion Ratio', 'Aff Earnings', 'Sales', 'EPC'])
            setCSVDataLabels(['affiliate_id', 'affiliate_name', 'clicks', 'conversions', 'conversion_ratio', 'affiliate_earnings', 'sales', 'epc'])
        } else {
            const baseHeaders = ['Affiliate Id', 'Affiliate', 'Clicks', 'Conversions', 'Conversion Ratio']
            const baseLabels = ['affiliate_id', 'affiliate_name', 'clicks', 'conversions', 'conversion_ratio']
            if (hasForex) {
                setCSVHeaderLabels([...baseHeaders, 'Net Earnings (FOREX)', 'Net Earnings', 'Aff Earnings', 'Sales', 'Margin', 'NPM', 'EPC', 'nEPC'])
                setCSVDataLabels([...baseLabels, 'orig_net_earnings', 'network_earnings', 'affiliate_earnings', 'sales', 'margin', 'margin_ratio', 'epc', 'nepc'])
            } else {
                setCSVHeaderLabels([...baseHeaders, 'Net Earnings', 'Aff Earnings', 'Sales', 'Margin', 'NPM', 'EPC', 'nEPC'])
                setCSVDataLabels([...baseLabels, 'network_earnings', 'affiliate_earnings', 'sales', 'margin', 'margin_ratio', 'epc', 'nepc'])
            }
        }
    }, [hideNetwork, hasForex])

    const handleClose = () => {
        setPullAffiliateModalOpen(false)
    }

    const openModal = (pullAffiliateData) => {
        setPullAffiliateModalOpen(true)
        setPullAffiliateModalData(pullAffiliateData)
    }

    const getReport = async () => {
        setDataLoaded(false)
        // We grab the data using the INITIALIZED dates. Hitting refresh turns selected options into init options.
        let url = buildUrl(`reports/affiliates?startDate=${initStartDate}&endDate=${initEndDate}`)
          
        callAPI(url, 'GET')
            .then((result) => {
                if (result['success'] === true) {
                    result.affiliate && setAffiliate(result.affiliate)
                    result.campaign && setCampaign(result.campaign)
                    result.advertiser && setAdvertiser(result.advertiser)
                    setReport(result.report)
                    setTotals(result.totals)
                    setHasForex(!!result.hasForex)
                    setForexCurrency(result.forexCurrency || null)
                    setCSVReport(result.report)
                    setCSVFilename(`Affiliate Report - ${initStartDate}-${initEndDate}`)
                    setDataLoaded(true)
                } else if (result['success'] === false) {
                    showSystemMessage('error', result.errors.message)
                    setDataLoaded(true)
                } else {
                    showSystemMessage('error', 'Could not load report.')
                    setDataLoaded(true)
                }
            })
            .catch((error) => {
                console.log(`Error while fetching affiliate report: ${error.message}`)
                showSystemMessage('error', `An error occurred while fetching affiliate report: ${error.message}`)
                setDataLoaded(true)
            })
    }

    const getReportOutput = () => {
        let filtered = [...report]

        if (marginFilterValue && marginFilterOperator) {
            const parsedValue = parseFloat(marginFilterValue)
            if (!isNaN(parsedValue)) {
                if (marginFilterOperator === 'GT') {
                    filtered = filtered.filter(item => item.margin > parsedValue)
                } else if (marginFilterOperator === 'LT') {
                    filtered = filtered.filter(item => item.margin < parsedValue)
                }
            }
        }

        const showForexColumn = hideNetwork !== 'true' && hasForex

        let columns = [
            {
                Header: 'Affiliate Id',
                sortType: numberSort,
                accessor: 'affiliate_id',
                Cell: ({ cell: { value } }) => {
                    return <>{value}</>
                },
            },
            {
                Header: 'Affiliate',
                accessor: 'affiliate_name',
                Cell: (tableData) => {
                    return <Affiliate aid={tableData.data[tableData.row.index].affiliate.id} name={tableData.data[tableData.row.index].affiliate.name} priority={tableData.data[tableData.row.index].affiliate.priority.value} status={tableData.data[tableData.row.index].affiliate.status.value} affiliateStatus={tableData.data[tableData.row.index].affiliate_status} holdPayment={tableData.data[tableData.row.index].hold_payment} campaignId={campaignId} />
                },
            },
            {
                Header: () => <div style={{ textAlign: 'right' }}>Clicks</div>,
                sortType: numberSort,
                accessor: 'clicks',
                className: 'text-right',
                Cell: (tableData) => {
                    return <Link to={buildUrl(`/reports/stats/daily?startDate=${initStartDate}&endDate=${initEndDate}&affiliateId=${tableData.data[tableData.row.index].affiliate_id}`)}>{`${Intl.NumberFormat('en-GB').format(tableData.data[tableData.row.index].clicks)}`}</Link>
                }
            },
            {
                Header: () => <div style={{ textAlign: 'right' }}>Conversions</div>,
                sortType: numberSort,
                accessor: 'conversions',
                className: 'text-right',
                Cell: (tableData) => {
                    return <Link to={buildUrl(`/reports/stats/conversions?startDate=${initStartDate}&endDate=${initEndDate}&affiliateId=${tableData.data[tableData.row.index].affiliate_id}`)}>{`${Intl.NumberFormat('en-GB').format(tableData.data[tableData.row.index].conversions)}`}</Link>
                },
            },
            {
                Header: () => <div style={{ textAlign: 'right' }}>CR %</div>,
                sortType: numberSort,
                accessor: 'conversion_ratio',
                className: 'text-right',
                Cell: ({ cell: { value } }) => {
                    return <>{!isNaN(value) ? <Number value={value} decimals={2} /> : 0.00} %</>
                },
            },
            ...(showForexColumn
                ? [
                      {
                          Header: () => <div style={{ textAlign: 'right' }}>
                            Net Earnings ({(forexCurrency || 'FOREX').toUpperCase()})
                        </div>,
                          sortType: numberSort,
                          accessor: 'orig_net_earnings',
                          className: 'text-right',
                          Cell: ({ cell: { value } }) => {
                              return <Dollars currency={(forexCurrency || 'USD')} value={value} />
                          },
                      },
                  ]
                : []),
            {
                Header: () => <div style={{ textAlign: 'right' }}>Net Earnings</div>,
                sortType: numberSort,
                accessor: 'network_earnings',
                className: 'text-right',
                Cell: ({ cell: { value } }) => {
                    return <Dollars value={value} />
                },
            },
            {
                Header: () => <div style={{ textAlign: 'right' }}>Aff Earnings</div>,
                sortType: numberSort,
                accessor: 'affiliate_earnings',
                className: 'text-right',
                Cell: ({ cell: { value } }) => {
                    return <Dollars value={value} />
                },
            },
            {
                Header: () => <div style={{ textAlign: 'right' }}>Sales</div>,
                sortType: numberSort,
                accessor: 'sales',
                className: 'text-right',
                Cell: ({ cell: { value } }) => {
                    return <Dollars value={value} />
                },
            },
            {
                Header: () => <div style={{ textAlign: 'right' }}>Margin</div>,
                sortType: numberSort,
                accessor: 'margin',
                className: 'text-right',
                Cell: ({ cell: { value } }) => {
                    return <Dollars value={value} />
                },
            },
            {
                Header: () => <div style={{ textAlign: 'right' }}>NPM</div>,
                sortType: numberSort,
                accessor: 'margin_ratio',
                className: 'text-right',
                sortType: 'basic',
                Cell: ({ cell: { value } }) => {
                    return <><Number value={value * 100} decimals={2} />%</>
                },
            },
            {
                Header: () => <div style={{ textAlign: 'right' }}>Contribution</div>,
                sortType: numberSort,
                accessor: 'contribution',
                className: 'text-right',
                sortType: 'basic',
                Cell: ({ cell: { value } }) => {
                    return <><Number value={value * 100} decimals={2} />%</>
                },
            },
            {
                Header: () => <div style={{ textAlign: 'right' }}>EPC</div>,
                sortType: numberSort,
                accessor: 'epc',
                className: 'text-right',
                Cell: ({ cell: { value } }) => {
                    return <Dollars value={value} />
                },
            },
            {
                Header: () => <div style={{ textAlign: 'right' }}>nEPC</div>,
                sortType: numberSort,
                accessor: 'nepc',
                className: 'text-right',
                Cell: ({ cell: { value } }) => {
                    return <Dollars value={value} />
                },
            },
            {
                Header: ' ',
                className: 'text-right',
                Cell: (tableData) => {
                    return <ReportsStatsDropdown reportType="affiliates" affiliateId={tableData.data[tableData.row.index].affiliate_id} campaignId={campaignId} advertiserId={advertiserId} withAvatar={withAvatar} navigation={navigation} showSystemMessage={showSystemMessage} openModal={openModal} />
                },
            },
        ]

        if (hideNetwork == 'true') {
            columns = [
                {
                    Header: 'Id',
                    sortType: numberSort,
                    accessor: 'affiliate_id',
                    Cell: ({ cell: { value } }) => {
                        return <>{value}</>
                    },
                },
                {
                    Header: 'Affiliate',
                    accessor: 'affiliate_name',
                    Cell: (tableData) => {
                        return <Affiliate aid={tableData.data[tableData.row.index].affiliate.id} name={tableData.data[tableData.row.index].affiliate.name} priority={tableData.data[tableData.row.index].affiliate.priority.value} status={tableData.data[tableData.row.index].affiliate.status.value} affiliateStatus={tableData.data[tableData.row.index].affiliate_status} campaignId={campaignId} />
                    },
                },
                {
                    Header: () => <div style={{ textAlign: 'right' }}>Clicks</div>,
                    sortType: numberSort,
                    accessor: 'clicks',
                    className: 'text-right',
                    Cell: (tableData) => {
                        return <Link to={buildUrl(`/reports/stats/daily?startDate=${initStartDate}&endDate=${initEndDate}&affiliateId=${tableData.data[tableData.row.index].affiliate_id}`)}>{`${Intl.NumberFormat('en-GB').format(tableData.data[tableData.row.index].clicks)}`}</Link>
                    }
                },
                {
                    Header: () => <div style={{ textAlign: 'right' }}>Conversions</div>,
                    sortType: numberSort,
                    accessor: 'conversions',
                    className: 'text-right',
                    Cell: (tableData) => {
                        return <Link to={buildUrl(`/reports/stats/conversions?startDate=${initStartDate}&endDate=${initEndDate}&affiliateId=${tableData.data[tableData.row.index].affiliate_id}`)}>{`${Intl.NumberFormat('en-GB').format(tableData.data[tableData.row.index].conversions)}`}</Link>
                    },
                },
                {
                    Header: () => <div style={{ textAlign: 'right' }}>CR %</div>,
                    sortType: numberSort,
                    accessor: 'conversion_ratio',
                    className: 'text-right',
                    Cell: ({ cell: { value } }) => {
                        return <><Number value={value} /> %</>
                    },
                },
                {
                    Header: () => <div style={{ textAlign: 'right' }}>Aff Earnings</div>,
                    sortType: numberSort,
                    accessor: 'affiliate_earnings',
                    className: 'text-right',
                    Cell: ({ cell: { value } }) => {
                        return <Dollars value={value} />
                    },
                },
                {
                    Header: () => <div style={{ textAlign: 'right' }}>Sales</div>,
                    sortType: numberSort,
                    accessor: 'sales',
                    className: 'text-right',
                    Cell: ({ cell: { value } }) => {
                        return <Dollars value={value} />
                    },
                },
                {
                    Header: () => <div style={{ textAlign: 'right' }}>EPC</div>,
                    sortType: numberSort,
                    accessor: 'epc',
                    className: 'text-right',
                    Cell: ({ cell: { value } }) => {
                        return <Dollars value={value} />
                    },
                },
                {
                    Header: ' ',
                    className: 'text-right',
                    Cell: (tableData) => {
                        return <ReportsStatsDropdown reportType="affiliates" affiliateId={tableData.data[tableData.row.index].affiliate_id} campaignId={campaignId} advertiserId={advertiserId} withAvatar={withAvatar} navigation={navigation} showSystemMessage={showSystemMessage} openModal={openModal} />
                    },
                },
            ]
        }

        return (
            <React.Fragment>
                <ReportTable data={filtered} columns={columns} totals={totals} />
            </React.Fragment>
        )
    }

    return (
        <div>
            {dataLoaded ? (
                report.length === 0 ? (
                    <div className="p-3">No stats for the current parameters.</div>
                ) : (
                    <React.Fragment>
                        {pullAffiliateModalOpen && <PullAffiliateModal data={pullAffiliateModalData} showSystemMessage={showSystemMessage} isOpen={pullAffiliateModalOpen} handleClose={handleClose} advertiserId={advertiserId} />}
                        {getReportOutput()}
                    </React.Fragment>
                )
            ) : (
                <Loading></Loading>
            )}
        </div>
    )
}